/**
 * useMenu (Vue) — Category tree fetch with depth-configurable recursive GraphQL query.
 *
 * Covers: Menu component.
 *
 * Responsibilities:
 * - Dynamic recursive GraphQL category query (depth-configurable, default 3)
 * - localStorage cache with 12h TTL, user-specific cache key
 * - Maps LocalizedString arrays to flat name/slug strings per language
 */

import { ref, type Ref } from 'vue';
import type { GraphQLClient } from '@propeller-commerce/propeller-sdk-v2';

// ── Types ─────────────────────────────────────────────────────────────────────

/** Raw category shape returned by the recursive GraphQL query */
interface MenuCategoryRaw {
  categoryId: number;
  hidden?: boolean | 'Y' | 'N' | string | null;
  // Every translation, not just the active language — see `mapCategory`.
  names: Array<{ value: string; language: string }>;
  slugs: Array<{ value: string; language: string }>;
  categories?: MenuCategoryRaw[];
}
function isHidden(raw: MenuCategoryRaw): boolean {
  return raw.hidden === true || raw.hidden === 'Y';
}

export interface MenuCategory {
  categoryId: number;
  name: string;
  slug: string;
  children: MenuCategory[];
}

export interface UseMenuOptions {
  graphqlClient: GraphQLClient;
  language?: Ref<string>;
  /** Nesting depth for the category tree. Default: 3. */
  depth?: number;
  /** Cache TTL in milliseconds. Default: 12h. */
  cacheTtlMs?: number;
}

export interface UseMenuReturn {
  categories: Ref<MenuCategory[]>;
  loading: Ref<boolean>;
  error: Ref<string | null>;
  fetchMenu: (rootCategoryId: number, userKey?: string) => Promise<void>;
  clearCache: (rootCategoryId: number, language: string, userKey?: string) => void;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const CACHE_TTL_DEFAULT = 12 * 60 * 60 * 1000; // 12h

// Module-level inflight dedup map — prevents two concurrent fetchMenu calls for
// the same key (e.g. Menu + HomeFallback both mounting at the same time) from
// both hitting the API. The second caller awaits the promise and reads from cache.
const inflightFetches = new Map<string, Promise<void>>();

// ── Pure helpers (module-level, no reactive deps) ─────────────────────────────

/**
 * Builds recursive `categories { ... }` fragment string for the GraphQL query.
 */
function buildCategoriesQuery(depth: number): string {
  if (depth === 0) return '';
  return `
    categories {
      categoryId
      hidden
      names { value language }
      slugs { value language }
      ${buildCategoriesQuery(depth - 1)}
    }
  `;
}

/**
 * Maps a raw SDK category (LocalizedString arrays) to a flat MenuCategory.
 * Picks the entry matching `language`, falls back to the first translation that
 * exists.
 *
 * That fallback only works because the query asks for `names`/`slugs` with NO
 * language argument, so every translation comes back. It used to filter
 * server-side — `names(language: $language)` — which meant a category with no
 * translation in the active language returned an EMPTY array, `[0]` was
 * `undefined` too, and the row rendered with a blank label and an empty slug:
 * invisible and unclickable. An untranslated category now falls back to the
 * name it does have rather than disappearing from the menu.
 */
function mapCategory(raw: MenuCategoryRaw, language: string): MenuCategory {
  const nameEntry = raw.names?.find(n => n.language === language) ?? raw.names?.[0];
  const slugEntry = raw.slugs?.find(s => s.language === language) ?? raw.slugs?.[0];
  return {
    categoryId: raw.categoryId,
    name: nameEntry?.value ?? '',
    slug: slugEntry?.value ?? '',
    children: (raw.categories ?? [])
      .filter(child => !isHidden(child))
      .map(child => mapCategory(child, language)),
  };
}

// ── Composable ────────────────────────────────────────────────────────────────

export function useMenu(options: UseMenuOptions): UseMenuReturn {
  const { graphqlClient } = options;
  const languageRef = options.language ?? ref('NL');
  const depth = options.depth ?? 3;
  const cacheTtlMs = options.cacheTtlMs ?? CACHE_TTL_DEFAULT;

  const categories = ref<MenuCategory[]>([]) as Ref<MenuCategory[]>;
  const loading = ref(false);
  const error = ref<string | null>(null);

  // ── Cache helpers ──────────────────────────────────────────────────────────

  function cacheKey(categoryId: number, lang: string, userKey = ''): string {
    return `propeller_menu_${categoryId}_${lang}${userKey ? `_${userKey}` : ''}`;
  }

  function getFromCache(key: string): MenuCategory[] | null {
    if (typeof window === 'undefined') return null;
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const parsed: { data: MenuCategory[]; expiresAt: number } = JSON.parse(raw);
      // Reject stale format (old Menu.tsx stored { data: Category, expires: ... })
      if (!Array.isArray(parsed.data)) { localStorage.removeItem(key); return null; }
      if (Date.now() > parsed.expiresAt) { localStorage.removeItem(key); return null; }
      return parsed.data;
    } catch { return null; }
  }

  function saveToCache(key: string, data: MenuCategory[]): void {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(key, JSON.stringify({ data, expiresAt: Date.now() + cacheTtlMs }));
    } catch { /* localStorage quota exceeded — silently ignore */ }
  }

  function clearCache(rootCategoryId: number, lang: string, userKey = ''): void {
    if (typeof window === 'undefined') return;
    try { localStorage.removeItem(cacheKey(rootCategoryId, lang, userKey)); } catch {}
  }

  // ── Fetch ──────────────────────────────────────────────────────────────────

  async function fetchMenu(rootCategoryId: number, userKey = ''): Promise<void> {
    const lang = languageRef.value || 'NL';
    const key = cacheKey(rootCategoryId, lang, userKey);
    const cached = getFromCache(key);
    if (cached) { categories.value = cached; return; }

    // Piggyback on an in-flight fetch for the same key instead of firing a
    // duplicate request (e.g. Menu + HomeFallback both mounting simultaneously).
    if (inflightFetches.has(key)) {
      loading.value = true;
      await inflightFetches.get(key);
      loading.value = false;
      const fresh = getFromCache(key);
      if (fresh) categories.value = fresh;
      return;
    }

    loading.value = true;
    error.value = null;

    let resolve!: () => void;
    const promise = new Promise<void>(res => { resolve = res; });
    inflightFetches.set(key, promise);

    try {
      // Build recursive query via buildCategoriesQuery() + the query string
      const gql = `
        query Menu($categoryId: Float) {
          category(categoryId: $categoryId) {
            categoryId
            hidden
            names { value language }
            slugs { value language }
            ${buildCategoriesQuery(depth)}
          }
        }
      `;
      const variables: Record<string, unknown> = { categoryId: rootCategoryId };

      // graphqlClient.query() extracts .data and throws on GraphQL errors
      const data = await graphqlClient.query<{ category: MenuCategoryRaw }>(gql, variables);
      const root = data?.category ?? null;

      // Return subcategories of root (L1 items) via getSubCategories(rootCategory)
      const items: MenuCategory[] = root
        ? (root.categories ?? [])
          .filter(cat => !isHidden(cat))
          .map(cat => mapCategory(cat, lang))
        : [];

      categories.value = items;
      if (items.length > 0) saveToCache(key, items);
    } catch (e: unknown) {
      error.value = e instanceof Error ? e.message : 'Failed to fetch menu';
    } finally {
      inflightFetches.delete(key);
      resolve();
      loading.value = false;
    }
  }

  return { categories, loading, error, fetchMenu, clearCache };
}
